import {Observable, Subject} from 'rxjs'
import {MultiAnswerFormGroup} from './multi-answer-form.group'
import {SingleAnswerFormGroup} from './single-answer-form.group'
import {AbstractControl, UntypedFormGroup} from '@angular/forms'
import {Answer, AnswerFormGroup, Question} from './dynamic-form'

export class DynamicForm extends UntypedFormGroup {
  private onTouched = new Subject<void>()
  declare value: FormState

  constructor(questions: Question[], defaultAnswers?: Answer[]) {
    super(DynamicForm.toControls(questions, defaultAnswers))
  }

  get onTouched$(): Observable<void> {
    return this.onTouched.asObservable()
  }

  get answerFormGroups(): AnswerFormGroup[] {
    return Object.values(this.controls) as (SingleAnswerFormGroup | MultiAnswerFormGroup)[]
  }

  setControls(questions: Question[], defaultAnswers?: Answer[]): void {
    this.controls = DynamicForm.toControls(questions, defaultAnswers)
  }

  toAnswers(): Answer[] {
    return this.answerFormGroups
      .map(answerModel => {
        const a = answerModel.toAnswer()
        if (a.answerIds?.length || a.answerValue) {
          return a
        }

        return undefined
      })
      .filter(a => !!a)
      .map(a => a as Answer)
  }

  override markAllAsTouched(): void {
    super.markAllAsTouched()
    this.onTouched.next()
  }

  override markAsTouched(): void {
    super.markAsTouched()
    this.onTouched.next()
  }

  private static toControls(questions: Question[], defaultAnswers?: Answer[]): Record<string, AbstractControl> {
    return questions
      .map(question => {
        const answerValue = defaultAnswers?.find(a => a.questionId === question.id)
        if (question.type === 'MULTIPLE_SELECT_CHECKBOXES' || question.type === 'MULTIPLE_SELECT_TILES') {
          return new MultiAnswerFormGroup(question, answerValue)
        } else {
          return new SingleAnswerFormGroup(question, answerValue)
        }
      })
      .reduce(
        (accumulator, formGroup) => ({
          ...accumulator,
          //Object.values(this.controls) automatically orders by control name, thus we use the question {order}-{id}
          //as control name
          [`${formGroup.question.order}-${formGroup.question.id}`]: formGroup,
        }),
        {},
      )
  }
}

export type FormState = Record<string, {answers: string[] | string; additionalInformation: string}>
